import React, { useContext, useEffect, useRef, useState } from 'react'
import DropDownV2 from '../../../global/ui/DropDownV2'
import { emptyFunction, getCompanyName, isVideo, showToast } from '../../../utils';
import { makeGetCall, makeNextPageCall, makePostCall } from '../../../effects/requests';
import { endPoints } from '../../../utils/endpoints';
import { ApiResponseType } from '../../../types';
import SuccessCreate from '../../../global/ui/SuccessCreate';
import { MyContext } from '../../../context';
import { apiConstants } from '../../../utils/apiConstants';
import CustomVideo from '../../../global/ui/CustomVideo';

const FolderIcon = ({ active }: { active: boolean }) => (
    <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill={active ? '#6C3CE9' : '#F5C518'}
        stroke="none"
    >
        <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z" />
    </svg>
);

const AsignLocationToCollection = ({ onClose, locationData, onCreate, production_company_name }: any) => {

    const [collectionList, setCollectionList] = useState([])
    const [selectedCollection, setSelectedCollection] = useState<any>(null)
    const [showSuccessModal, setShowSuccessModal] = useState(false);

    const { userData } = useContext(MyContext);

    const [allLocationData, setAllLocationData] = useState<any>([]);
    const [selectedLocation, setSelectedLocation] = useState<any>(null);
    const [nextUrl, setNextUrl] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState<boolean | null>(false);

    const [allFolderData, setAllFolderData] = useState<any[]>([]);
    const [activeGallery, setActiveGallery] = useState<string | null>(null);
    const [selectedImageIds, setSelectedImageIds] = useState<string[]>([]);

    const folderRequestRef = useRef(0);

    // Get images from selected folder
    const selectedFolder = allFolderData.find((folder) => folder.id === activeGallery);

    const images = selectedFolder?.images || [];

    const onSubmit = () => {
        if (!selectedLocation) {
            showToast('Location is required.', 'error');
            return
        }
        if (!selectedCollection) {
            showToast('Collection is required.', 'error');
            return
        }
        if (selectedImageIds?.length === 0) {
            showToast('Please select at least one image.', 'error');
            return;
        }
        const fromData = {
            collection_id: selectedCollection?.id,
            image_ids: selectedImageIds,
            location_id: locationData?.location?.id
        }
        makePostCall(endPoints.LOCATION_COLLECTION_IMAGES, fromData).then((res: ApiResponseType<any>) => {
            if (res?.status?.code == 200) {
                setShowSuccessModal(true)
            }
        })
    };

    const fetchCollection = () => {
        makeGetCall(endPoints.LOCATION_COLLECTION, { production_company_name: production_company_name }).then((res: ApiResponseType<any>) => {
            if (res?.status?.code == 200) {
                const data = res?.results?.length > 0 ? res?.results : [];
                data?.map((item: any) => {
                    item.label = item?.name || ''
                    item.value = item?.id || ''
                    return item
                })
                setCollectionList(data)
            }
        })
    }

    const formatRes = (results: any) => {
        const data = (results || [])?.map((item: any) => ({
            ...item,
            label: item?.location?.name,
            value: item?.location?.id
        }));
        return data
    }

    const handleImageSelect = (imageId: string) => {
        setSelectedImageIds((prev) =>
            prev.includes(imageId)
                ? prev.filter((id) => id !== imageId)
                : [...prev, imageId]
        );
    };

    // Fetch all locations
    useEffect(() => {
        if (!userData?.id) return;

        const params = {
            production_company_name: getCompanyName(userData),
            search: '',
            my_vault: false
        };

        makeGetCall(endPoints.LOCATION_VAULT, params)
            .then((res: ApiResponseType<any>) => {
                if (res?.status?.code === 200) {
                    const data = formatRes(res?.results)
                    setAllLocationData(data);
                    setNextUrl(res?.next || null);
                }
            })
            .catch(emptyFunction);
    }, [userData?.id]);

    // Fetch folders by selected location
    const fetchFolders = (locationId: string) => {
        const requestId = ++folderRequestRef.current;
        setAllFolderData([]);
        setActiveGallery(null);
        setSelectedImageIds([]);

        makeGetCall(endPoints.LOCATION_FOLDER, { location_id: locationId })
            .then((res: ApiResponseType<any>) => {
                if (requestId !== folderRequestRef.current) return;
                if (res?.status?.code === 200) {
                    setAllFolderData(res?.results || []);
                }
            })
            .catch(emptyFunction);
    };

    // Location selection
    const handleLocationSelect = (location: any) => {
        setSelectedLocation(location);
        setAllFolderData([]);
        setActiveGallery(null);
        setSelectedImageIds([]);
        if (location?.id) {
            fetchFolders(location?.id);
        } else {
            ++folderRequestRef.current;
        }
    };

    // Folder selection
    const handleFolderSelect = (folderId: string) => {
        setActiveGallery(folderId);
        // setSelectedImageIds([]);
    };

    const fetchNextPage = () => {
        if (!nextUrl) return;
        setIsLoading(true)
        makeNextPageCall(nextUrl, false, true).then((res: ApiResponseType<any>) => {
            setIsLoading(false);
            const data = formatRes(res?.results)
            setAllLocationData([...allLocationData, ...data]);
            setNextUrl(res?.next);
        }).catch(() => setIsLoading(false))
    }

    useEffect(() => {
        fetchCollection();
    }, [])

    useEffect(() => {
        if (locationData?.id) {
            const data = formatRes([locationData]);
            if (data?.[0]) {
                handleLocationSelect(data?.[0])
            }
        }
    }, [locationData?.id])

    const isAllSelected = images?.length > 0 && images?.every((img: any) => selectedImageIds?.includes(img?.id));

    const handleSelectAll = () => {
        const currentFolderImageIds = images?.map((img: any) => img?.id);

        setSelectedImageIds((prev) => {
            if (isAllSelected) {
                // Remove only images belonging to the current folder
                return prev?.filter((id) => !currentFolderImageIds?.includes(id));
            }

            // Add all images from the current folder without duplicates
            return [...new Set([...prev, ...currentFolderImageIds])];
        });
    };

    return (
        <div>
            {/* Backdrop */}
            <div className="cl-backdrop" />

            {/* Drawer panel */}
            <div className="cl-drawer">

                {/* Header */}
                <div className="cl-drawer__header">
                    <button className="cl-drawer__close" onClick={onClose}>
                        <img src='/assets/images/dismiss.svg' alt='close' />
                    </button>
                    <h2 className="cl-drawer__title">Assign to a collection</h2>
                </div>

                {/* Step content */}
                <div className="cl-drawer__body">
                    <div className="cs-step">

                        {/* Select Location */}
                        <DropDownV2
                            label="Locations"
                            placeholder="Please select location"
                            data={allLocationData}
                            selectedItems={selectedLocation}
                            onSelect={handleLocationSelect}
                            enablePagination={true}
                            onLoadMore={fetchNextPage}
                            hasMore={nextUrl ? true : false}
                            isLoading={isLoading ? true : false}
                            style={{ marginBottom: 15 }}
                        />

                        <DropDownV2
                            name={apiConstants.collection}
                            label="Collection"
                            placeholder="Select collection"
                            data={collectionList}
                            selectedItems={selectedCollection}
                            onSelect={setSelectedCollection}
                        />

                        {/* Select Galleries */}
                        {allFolderData?.length > 0 &&
                            <h3 className="cs-step__title cs-step__title--lg">
                                Select Galleries
                            </h3>
                        }
                        <div className="cs-gallery-grid">
                            {allFolderData.map((gallery) => {
                                const isActive = gallery.id === activeGallery;

                                return (
                                    <button
                                        type="button"
                                        key={gallery.id}
                                        className={`cs-gallery-card${isActive ? ' cs-gallery-card--active' : ''}`}
                                        onClick={() => handleFolderSelect(gallery.id)}
                                    >
                                        <FolderIcon active={isActive} />
                                        {gallery.name}
                                    </button>
                                );
                            })}
                        </div>

                        {/* Select Image */}
                        {(allFolderData?.length > 0 && activeGallery) &&
                            <div className="d-flex justify-content-between align-items-center mb-2 mt-4">
                                <h3 className="cs-step__title">
                                    Select Image
                                </h3>

                                <div className="form-check cs-select-all">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id="selectAllImages"
                                        checked={isAllSelected}
                                        onChange={handleSelectAll}
                                    />
                                    <label className="form-check-label" htmlFor="selectAllImages">
                                        Select all
                                    </label>
                                </div>
                            </div>
                        }

                        <div className="cs-image-grid">
                            {images.map((img: any) => {
                                const isSelected = selectedImageIds?.includes(img.id);

                                return (
                                    <button
                                        type="button"
                                        key={img.id}
                                        className="cs-image-tile"
                                        onClick={() => handleImageSelect(img?.id)}
                                    >
                                        {isVideo(img) ? (
                                            <CustomVideo
                                                src={img?.image}
                                                controls={false}
                                            />
                                        ) : (
                                            <img src={img.image} alt="" />
                                        )}
                                        <span
                                            className={`cs-image-tile__check${isSelected ? ' cs-image-tile__check--selected' : ''}`}
                                        >
                                            <svg
                                                width="12"
                                                height="12"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="3"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            >
                                                <polyline points="20 6 9 17 4 12" />
                                            </svg>
                                        </span>
                                    </button>
                                );
                            })}

                            {images.length === 0 && (
                                <p className="cs-empty-text">
                                    No images in this gallery yet.
                                </p>
                            )}
                        </div>
                    </div>
                </div>

                {/* Footer: action button */}
                <div className="cl-drawer__footer">
                    <button
                        type='button'
                        className="cl-btn-next"
                        onClick={onCreate}
                    >
                        Create New Collection
                    </button>
                    <button
                        type='button'
                        className="cl-btn-next"
                        onClick={onSubmit}
                    >
                        Assign
                    </button>
                </div>
            </div>
            {showSuccessModal &&
                <SuccessCreate
                    decription='Successfully assigned to collection.'
                    heading='Assigned to Collection'
                    onClose={() => {
                        setShowSuccessModal(false);
                        onClose?.()
                    }}
                />
            }
        </div>
    )
}

export default AsignLocationToCollection